import React, { forwardRef } from 'react';
import { Loader2Icon } from 'lucide-react';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  loading?: boolean;
  fullWidth?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  children: React.ReactNode;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      fullWidth = false,
      leftIcon,
      rightIcon,
      children,
      disabled,
      className = '',
      ...props
    },
    ref
  ) => {
  const baseClasses = 'inline-flex items-center justify-center font-patreon-medium rounded-full transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98] relative overflow-hidden';

    const variantClasses = {
      primary: 'text-patreon-white focus:ring-[color:var(--patreon-highlight)] hover:shadow-[var(--patreon-shadow-soft-lg)] hover:-translate-y-0.5',
      secondary: 'text-[color:var(--patreon-text-primary)] bg-[color:var(--patreon-white)] border border-[color:var(--patreon-neutral-30)] hover:border-[color:var(--patreon-neutral-50)] hover:bg-[color:var(--patreon-neutral-10)] hover:shadow-[var(--patreon-shadow-sm)] focus:ring-[color:var(--patreon-highlight)]',
      outline: 'border border-[color:var(--patreon-neutral-30)] text-[color:var(--patreon-text-primary)] bg-transparent hover:bg-[color:var(--patreon-neutral-10)] hover:border-[color:var(--patreon-neutral-50)] hover:shadow-[var(--patreon-shadow-sm)] focus:ring-[color:var(--patreon-highlight)]',
      ghost: 'text-[color:var(--patreon-text-primary)] hover:bg-[color:var(--patreon-neutral-10)] focus:ring-[color:var(--patreon-highlight)]',
      danger: 'bg-[color:var(--patreon-error)] text-patreon-white hover:bg-[color:var(--patreon-error-light)] hover:shadow-[var(--patreon-shadow-soft-lg)] hover:-translate-y-0.5 focus:ring-[color:var(--patreon-error)]',
    } as const;

    const sizeClasses = {
      xs: 'px-3 py-1.5 text-[var(--patreon-font-size-label-0)] gap-1.5',
      sm: 'px-4 py-2 text-[var(--patreon-font-size-label-1)] gap-2',
      md: 'px-6 py-3 text-[var(--patreon-font-size-label-2)] gap-2',
      lg: 'px-8 py-4 text-[var(--patreon-font-size-label-2)] gap-2.5',
      xl: 'px-10 py-5 text-[var(--patreon-font-size-label-3)] gap-3',
    };

    const iconSizeClasses = {
      xs: 'w-3 h-3',
      sm: 'w-4 h-4',
      md: 'w-4 h-4',
      lg: 'w-5 h-5',
      xl: 'w-6 h-6',
    };

    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        className={`
          ${baseClasses}
          ${variantClasses[variant]}
          ${sizeClasses[size]}
          ${fullWidth ? 'w-full' : ''}
          ${className}
        `}
        style={
          variant === 'primary'
            ? {
                background: 'var(--patreon-btn-primary-bg)',
                color: 'var(--patreon-btn-primary-text)',
                boxShadow: 'var(--patreon-btn-primary-shadow)',
                fontFamily: 'var(--patreon-font-primary)',
                fontWeight: 'var(--patreon-font-weight-medium)',
                transitionDuration: 'var(--patreon-duration-normal)',
                transitionTimingFunction: 'var(--patreon-ease-out)',
              }
            : {
                fontFamily: 'var(--patreon-font-primary)',
                transitionDuration: 'var(--patreon-duration-normal)',
                transitionTimingFunction: 'var(--patreon-ease-out)',
              }
        }
        {...props}
      >
        {loading ? (
          <Loader2Icon className={`animate-spin ${iconSizeClasses[size]}`} />
        ) : leftIcon ? (
          <span className={`${rightIcon ? 'mr-2' : children ? 'mr-2' : ''} ${iconSizeClasses[size]}`}>
            {leftIcon}
          </span>
        ) : null}

        {children}

        {rightIcon && !loading && (
          <span className={`ml-2 ${iconSizeClasses[size]}`}>
            {rightIcon}
          </span>
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;
